import { c as _c } from "react/compiler-runtime";
import { useEffect } from 'react';
import { create } from 'zustand';
import { useDeferredQueryEnabled } from '@/hooks/use-deferred-query-enabled';

/**
 * How long a PR view waits for its content to paint before starting deferred requests anyway,
 * for views that never mark it: a diff worker pool that never initializes, or a tab opened in the
 * background, where frames don't run until it is shown.
 */
export const CONTENT_PAINT_MAX_WAIT_MS = 5000;
type PrV2ContentPaintStore = {
  /** `performance.now()` at the latest content paint of each PR view. */
  paintedAtByPrKey: Record<string, number>;
  markPainted: (prKey: string) => void;
};

/**
 * Records which PR views have painted their content, keyed by `getPrV2ActiveFileStoreKey`. The
 * content is the first diff on the Changes tab and the timeline on the Discussion tab.
 *
 * Requests the first screen doesn't need wait for this signal so they don't compete with that
 * content for network, server and main-thread time.
 */
export const usePrV2ContentPaintStore = create<PrV2ContentPaintStore>(set => ({
  paintedAtByPrKey: {},
  // Every paint is recorded, so a later visit to the same PR in this tab has its own timestamp.
  markPainted: prKey => set(state => ({
    paintedAtByPrKey: {
      ...state.paintedAtByPrKey,
      [prKey]: performance.now()
    }
  }))
}));

/**
 * Marks the PR's content as painted once `isContentPainted` holds through a painted frame.
 */
export function useMarkPrV2ContentPainted(prKey, isContentPainted) {
  const $ = _c(5);
  const markPainted = usePrV2ContentPaintStore(_temp);
  let t0;
  let t1;
  if ($[0] !== isContentPainted || $[1] !== markPainted || $[2] !== prKey) {
    t0 = () => {
      if (!isContentPainted) {
        return;
      }
      let timeoutId;
      const frameId = window.requestAnimationFrame(() => {
        timeoutId = window.setTimeout(() => markPainted(prKey), 0);
      });
      return () => {
        window.cancelAnimationFrame(frameId);
        window.clearTimeout(timeoutId);
      };
    };
    t1 = [isContentPainted, markPainted, prKey];
    $[0] = isContentPainted;
    $[1] = markPainted;
    $[2] = prKey;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
}

/**
 * Whether the PR's content has painted, or `CONTENT_PAINT_MAX_WAIT_MS` has passed since the
 * caller mounted.
 */
function _temp(state) {
  return state.markPainted;
}
export function usePrV2ContentPainted(prKey) {
  const $ = _c(2);
  let t0;
  if ($[0] !== prKey) {
    t0 = state => state.paintedAtByPrKey[prKey] !== undefined;
    $[0] = prKey;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const isPainted = usePrV2ContentPaintStore(t0);
  const hasWaitedForPaint = useDeferredQueryEnabled(CONTENT_PAINT_MAX_WAIT_MS);
  return isPainted || hasWaitedForPaint;
}